import React, { useRef, useState, useEffect } from "react";
import "../scoreScale/ScoreScale.css";
import { getAllAnswerValues } from '@formsort/custom-question-api';
import { getTopSymptoms, getChildSection, getPreteenSection, getAdultSection } from "../../../helpers/utils";


const ScoreScale = () => {
  const [age, setAge] = useState(null);
  const [score, setScore] = useState(null);
  const [topSymptoms, setTopSymptoms] = useState([]);
  const sliderBarRef = useRef(null);
  const pointerRef = useRef(null);


  useEffect(() => {
    getAllAnswerValues().then(answers => {
      const age_category = answers['age_category'];
      const preTeenScore = answers['9to13age_bvdq_scores'];
      const childScore = answers['4to8age_bvdq_scores'];
      const adultScore = answers['above13age_bvdq_scores'];

      setAge(age_category);
      setScore(preTeenScore || childScore || adultScore || null);

      const topSymptoms = getTopSymptoms(answers || []);
      setTopSymptoms(topSymptoms);
    });
  }, []);


  const adultMaxScore = 75;
  const preTeenMaxScore = 48;
  const childMaxScore = 13;

  const adultPointerPosition =
    score !== "--" && score >= 0 && score <= adultMaxScore
      ? (score / adultMaxScore) * 100
      : 0;

  const preteenPointerPosition =
    score !== "--" && score >= 0 && score <= preTeenMaxScore
      ? (score / preTeenMaxScore) * 100
      : 0;

  const childPointerPosition =
    score !== "--" && score >= 0 && score <= childMaxScore
      ? (score / childMaxScore) * 100
      : 0;


  const getPointerClassPreteen = () => {
    if (preteenPointerPosition <= (7 / 48) * 100) {
      return "pointer-left";
    } else if (preteenPointerPosition >= (43 / 48) * 100) {
      return "pointer-right";
    } else {
      return "pointer-text";
    }
  };

  const getPointerClassChild = () => {
    if (childPointerPosition <= (1 / 13) * 100) {
      return "pointer-left";
    } else if (childPointerPosition >= (12 / 13) * 100) {
      return "pointer-right";
    } else {
      return "pointer-text";
    }
  };

  const getPointerClassAdult = () => {
    if (adultPointerPosition <= (12 / 75) * 100) {
      return "pointer-left";
    } else if (adultPointerPosition >= (67 / 75) * 100) {
      return "pointer-right";
    } else {
      return "pointer-text";
    }
  };


  const childSection = getChildSection(score);
  const preteenSection = getPreteenSection(score);
  const adultSection = getAdultSection(score);

  // Content for age 3-8
  const renderChildCard = () => (
    <>
      <div className="text-center pb-3">
        <h3 className="page-col">{score || 0} out of 13</h3>
        <strong>
          <h1 className="main-heading-col">
            {childSection.text === "Unlikely" ? (
              <>
                Your child is <span className={childSection.className}>Unlikely</span> to have BVD
              </>
            ) : (
              <>
                Your child likely has
                <span className={childSection.className}> {childSection.text}</span> BVD
              </>
            )}
          </h1>
        </strong>

        <p className="pt-2">
          {childSection.text === "Unlikely" ? (
            <>
              Your child's score suggests your child <strong>likely does not have Binocular Vision Dysfunction</strong> and may not need our care.
            </>
          ) : (
            <>
              Your child’s score indicates that they likely have Binocular Vision Dysfunction and are a candidate for receiving our care.
            </>
          )}
        </p>
      </div>
      <div className="blockdig">
        <div className="custom-slider-container">
          <div className="custom-slider-bar" ref={sliderBarRef}>
            <div
              className="custom-slider-segment custom-unlikely"
              style={{
                left: 0,
                width: `calc((3 / 13) * 100%)`,
              }}
            >
              <div className="custom-slider-label custom-label-unlikely">
                Unlikely
                <br />
                <span>0-3</span>
              </div>
            </div>
            <div
              className="custom-slider-segment custom-mild"
              style={{
                left: `calc((3 / 13) * 100%)`,
                width: `calc((4 / 13) * 100%)`,
              }}
            >
              <div className="custom-slider-label custom-label-mild">
                Moderate
                <br />
                <span>4-7</span>
              </div>
            </div>
            <div
              className="custom-slider-segment custom-moderate"
              style={{
                left: `calc((7 / 13) * 100%)`,
                width: `calc((3 / 13) * 100%)`,
              }}
            >
              <div className="custom-slider-label custom-label-moderate">
                High
                <br />
                <span>8-10</span>
              </div>
            </div>
            <div
              className="custom-slider-segment custom-severe"
              style={{
                left: `calc((10 / 13) * 100%)`,
                width: `calc((3 / 13) * 100%)`,
              }}
            >
              <div className="custom-slider-label custom-label-severe">
                Severe
                <br />
                <span>11-13</span>
              </div>
            </div>
            <div
              className="custom-slider-pointer"
              ref={pointerRef}
              style={{ left: `calc(${childPointerPosition}% - 10px)` }}
            >
              <div className={`${getPointerClassChild()}`}>Where you fall</div>
            </div>
          </div>

        </div>
      </div>
      <div className="text-center pt-3">
        {topSymptoms.length > 0 ? (
          <>
            <h4 className="bottom-text">Your symptoms we aim to treat</h4>
            <div className="d-flex py-3 justify-content-center flex-wrap gap-3 mb-5">
              {topSymptoms.map((symptom, index) => (
                <span key={index} className="link-col">
                  {symptom.name}
                </span>
              ))}
            </div>
          </>
        ) : ''}
      </div>
    </>
  );




  // Content for age 8 to 13 age group
  const renderPreTeenCard = () => (
    <>
      <div className="text-center pb-3">
        <h3 className="page-col">{score || 0} out of 48</h3>
        <strong>
          <h1 className="main-heading-col">
            {preteenSection.text === "Unlikely" ? (
              <>
                You are <span className={preteenSection.className}>Unlikely</span> to have BVD
              </>
            ) : (
              <>
                You likely have
                <span className={preteenSection.className}> {preteenSection.text}</span> BVD
              </>
            )}
          </h1>
        </strong>
        <p className="pt-2">
          {preteenSection.text === "Unlikely" ? (
            <>
              Your score suggests you <strong>likely do not have Binocular Vision Dysfunction</strong> and may not need our care.
            </>
          ) : (
            <>
              Your score indicates that you likely have Binocular Vision
              Dysfunction and are a candidate for receiving our care.
            </>
          )}
        </p>
      </div>
      <div className="blockdig">
        <div className="custom-slider-container">
          <div className="custom-slider-bar" ref={sliderBarRef}>
            <div
              className="custom-slider-segment custom-unlikely"
              style={{
                left: 0,
                width: `calc((9 / 48) * 100%)`,
              }}
            >
              <div className="custom-slider-label custom-label-unlikely">
                Unlikely
                <br />
                <span>0-9</span>
              </div>
            </div>
            <div
              className="custom-slider-segment custom-mild"
              style={{
                left: `calc((9 / 48) * 100%)`,
                width: `calc((10 / 48) * 100%)`,
              }}
            >
              <div className="custom-slider-label custom-label-mild">
                Moderate
                <br />
                <span>10-19</span>
              </div>
            </div>
            <div
              className="custom-slider-segment custom-moderate"
              style={{
                left: `calc((19 / 48) * 100%)`,
                width: `calc((10 / 48) * 100%)`,
              }}
            >
              <div className="custom-slider-label custom-label-moderate">
                High
                <br />
                <span>20-29</span>
              </div>
            </div>
            <div
              className="custom-slider-segment custom-severe"
              style={{
                left: `calc((29 / 48) * 100%)`,
                width: `calc((19 / 48) * 100%)`,
              }}
            >
              <div className="custom-slider-label custom-label-severe">
                Severe
                <br />
                <span>30-48</span>
              </div>
            </div>
            <div
              className="custom-slider-pointer"
              ref={pointerRef}
              style={{ left: `calc(${preteenPointerPosition}% - 10px)` }}
            >
              <div className={`${getPointerClassPreteen()}`}>Where you fall</div>
            </div>
          </div>

        </div>
      </div>
      <div className="text-center pt-3">
        {topSymptoms.length > 0 ? (
          <>
            <h4 className="bottom-text">Your symptoms we aim to treat</h4>
            <div className="d-flex py-3 justify-content-center flex-wrap gap-3 mb-5">
              {topSymptoms.map((symptom, index) => (
                <span key={index} className="link-col">
                  {symptom.name}
                </span>
              ))}
            </div>
          </>
        ) : ''}
      </div>
    </>
  );

  // Content for age 14+
  const renderAdultCard = () => (
    <>
      <div className="text-center pb-3">
        <h3 className="page-col">{score || 0} out of 75</h3>
        <strong>
          <h1 className="main-heading-col">
            {adultSection.text === "Unlikely" ? (
              <>
                You are <span className={adultSection.className}>Unlikely</span> to have BVD
              </>
            ) : (
              <>
                You likely have
                <span className={adultSection.className}> {adultSection.text}</span> BVD
              </>
            )}
          </h1>
        </strong>

        {/* <p className="pt-2">
          Your score indicates that you likely have Binocular Vision
          Dysfunction and are a candidate for receiving our care.
        </p> */}
        <p className="pt-2">
          {adultSection.text === "Unlikely" ? (
            <>
              Your score suggests you <strong>likely do not have Binocular Vision Dysfunction</strong> and may not need our care.
            </>
          ) : (
            <>
              Your score indicates that you likely have Binocular Vision
              Dysfunction and are a candidate for receiving our care.
            </>
          )}
        </p>
      </div>
      <div className="blockdig">
        <div className="custom-slider-container">
          <div className="custom-slider-bar" ref={sliderBarRef}>
            <div
              className="custom-slider-segment custom-unlikely"
              style={{
                left: 0,
                width: `calc((14 / 75) * 100%)`,
              }}
            >
              <div className="custom-slider-label custom-label-unlikely">
                Unlikely
                <br />
                <span>0-14</span>
              </div>
            </div>
            <div
              className="custom-slider-segment custom-mild"
              style={{
                left: `calc((14 / 75) * 100%)`,
                width: `calc((16 / 75) * 100%)`,
              }}
            >
              <div className="custom-slider-label custom-label-mild">
                Moderate
                <br />
                <span>15-30</span>
              </div>
            </div>
            <div
              className="custom-slider-segment custom-moderate"
              style={{
                left: `calc((30 / 75) * 100%)`,
                width: `calc((15 / 75) * 100%)`,
              }}
            >
              <div className="custom-slider-label custom-label-moderate">
                High
                <br />
                <span>31-45</span>
              </div>
            </div>
            <div
              className="custom-slider-segment custom-severe"
              style={{
                left: `calc((45 / 75) * 100%)`,
                width: `calc((30 / 75) * 100%)`,
              }}
            >
              <div className="custom-slider-label custom-label-severe">
                Severe
                <br />
                <span>46-75</span>
              </div>
            </div>
            <div
              className="custom-slider-pointer"
              ref={pointerRef}
              style={{ left: `calc(${adultPointerPosition}% - 10px)` }}
            >
              <div className={`${getPointerClassAdult()}`}>Where you fall</div>
            </div>
          </div>

        </div>
      </div>
      <div className="text-center pt-3">
        {topSymptoms.length > 0 ? (
          <>
            <h4 className="bottom-text">Your symptoms we aim to treat</h4>
            <div className="d-flex py-3 justify-content-center flex-wrap gap-3 mb-5">
              {topSymptoms.map((symptom, index) => (
                <span key={index} className="link-col">
                  {symptom.name}
                </span>
              ))}
            </div>
          </>
        ) : ''}
      </div>
    </>
  );

  return (
    <div>
      <section className="py-4">
        <div className="container px-2">
          <div className="col-lg-12 py-3 px-3 col-sm-12 outer-wrapper">
            {age == "child" && renderChildCard()}
            {age == "youth" && renderPreTeenCard()}
            {age == "adult" && renderAdultCard()}
          </div>
        </div>
      </section>
    </div>
  );
};

export default ScoreScale;